import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useScriptCreation, TextSegmentData } from '../../contexts/ScriptCreationContext';
import { meditationParts } from '../../contexts/ScriptCreationContext';
import { Icon } from '../../components/common/Icon';
import { FaChevronUp, FaChevronDown, FaMagic, FaFolderOpen } from 'react-icons/fa';
import { FaWandMagicSparkles } from 'react-icons/fa6';
import { config } from '../../config';
import { authService } from '../../services/auth';
import { useCoins } from '../../contexts/UserContext';
import { HelpButton } from '../../components/common/HelpButton';
import { UseExistingModal } from '../../components/UseExistingModal';
import { scriptService } from '../../services/scriptService';

const helpContent = `
<div class="alert info">
   Write your meditation script, or get some help from AI.
   </br></br>
<ul>
<li>You can write your meditation script manually, or get some help from AI with the "Help me write it" button.</li>
<li>You can improve your script using AI with the "Improve my script" button.</li>
<li>Script text will be segmented into parts in the next step to enable fine-grained control over pause timings.</li>
<li>You can add pauses to your script by typing "[number]s" or "[number]m" (e.g. "[2s]" or "[1.5m]").</li>
<li>You can reorder parts to change the order of the script with the up and down arrows.</li>
<li>You can collapse parts to make the script easier to read with the "−" button.</li>
<li>Part names can be edited by clicking on the part name and typing.</li>
<li>Press the "Save and Continue" button to continue to the next step.</li>
</div>

`;

const Container = styled.div`
  //max-width: 1200px;
  margin: 0 auto;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ContentBox = styled.div`
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 12px;
  padding: 20px;
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const ContentTitleWithControls = styled.div`
  display: flex;
  align-items: center;
`;

const ReorderControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-right: 10px;
`;

const ReorderButton = styled.button`
  background-color: #444;
  color: #ffffff;
  border: none;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: #555;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContentTitleInput = styled.input`
  color: #ffffff;
  background: transparent;
  border: none;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  font-family: inherit;
  outline: none;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  min-width: 200px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  &:focus {
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid #007bff;
  }
`;

const ContentButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  background-color: #444;
  color: #ffffff;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background-color: #555;
  }
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;

  &:hover {
    background-color: #444;
  }
`;

const ContentEditor = styled.div`
  width: 100%;
  min-height: 120px;
  padding: 15px;
  border: 1px solid #444;
  border-radius: 8px;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 16px;
  line-height: 1.6;
  outline: none;
  cursor: text;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  box-sizing: border-box;

  &:focus {
    border-color: #007bff;
  }

  &:empty::before {
    content: "Start typing or paste your content here...";
    color: #666;
    font-style: italic;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #666;
  font-size: 16px;
  padding: 40px;
`;

const SegmentationModeContainer = styled.div`
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  margin-top: 20px;
`;

const SegmentationModeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const SegmentationModeTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const SegmentationModeOptions = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`;

const SegmentationModeOption = styled.div<{ $selected: boolean }>`
  padding: 12px 16px;
  border: 1px solid ${props => props.$selected ? '#007bff' : '#444'};
  border-radius: 8px;
  background-color: ${props => props.$selected ? 'rgba(0, 123, 255, 0.1)' : '#1a1a1a'};
  color: ${props => props.$selected ? '#007bff' : '#cccccc'};
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  
  &:hover {
    border-color: #007bff;
    background-color: rgba(0, 123, 255, 0.1);
  }
`;

const SegmentationModeDescription = styled.p`
  color: #888;
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
`;

const NextButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 20px;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #666;
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const WarningMessage = styled.div`
  background-color: #2d3436;
  border-left: 4px solid #f39c12;
  padding: 15px;
  margin-top: 30px;
  margin-bottom: 15px;
  border-radius: 12px;
  color: #e17055;
  font-size: 14px;
  line-height: 1.4;
`;

const ConfirmationModal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  align-items: center;
  justify-content: center;
  z-index: 1001;
`;

const ConfirmationContent = styled.div`
  background-color: #2a2a2a;
  padding: 30px;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  text-align: center;
`;

const ConfirmationTitle = styled.h3`
  color: #ffffff;
  margin: 0 0 15px 0;
  font-size: 18px;
`;

const ConfirmationText = styled.p`
  color: #cccccc;
  margin: 0 0 25px 0;
  line-height: 1.5;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
`;

const ConfirmationButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;
  
  ${props => props.variant === 'primary' ? `
    background-color: #e74c3c;
    color: white;
    &:hover {
      background-color: #c0392b;
    }
  ` : `
    background-color: #666;
    color: white;
    &:hover {
      background-color: #777;
    }
  `}
`;

// Modal components
const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #2a2a2a;
  padding: 30px;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 24px;
  cursor: pointer;
`;

const SettingGroup = styled.div`
  margin-bottom: 20px;
`;

const SettingLabel = styled.label`
  display: block;
  color: #ffffff;
  margin-bottom: 8px;
  font-weight: 600;
`;

const SettingInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #444;
  border-radius: 6px;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const SettingTextArea = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid #444;
  border-radius: 6px;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 14px;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;


// Custom editable content editor component
interface EditableContentEditorProps {
  partId: string;
  content: string;
  onChange: (content: string) => void;
  disabled?: boolean;
  onEditorClick?: (partId: string) => void;
}

const EditableContentEditor: React.FC<EditableContentEditorProps> = ({ partId, content, onChange, disabled = false, onEditorClick }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [lastContent, setLastContent] = useState(''); // Initialize as empty so useEffect runs
  const [lastPasteTime, setLastPasteTime] = useState(0);
  const [isUpdatingProgrammatically, setIsUpdatingProgrammatically] = useState(false);
  
  console.log(`EditableContentEditor render for ${partId}: content="${content}"`);

  // Original elegant approach from NewScript.tsx - improved for consistency
  const getTextWithLineBreaks = (element: HTMLElement): string => {
    let text = '';
    const childNodes = Array.from(element.childNodes);
    
    for (let i = 0; i < childNodes.length; i++) {
      const node = childNodes[i];
      const nextNode = i < childNodes.length - 1 ? childNodes[i + 1] : null;
      
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const elementNode = node as HTMLElement;
        
        // Handle <br> tags as single newlines
        if (elementNode.tagName === 'BR') {
          // Check if the next node is also a <br> to avoid double newlines
          if (nextNode && nextNode.nodeType === Node.ELEMENT_NODE && 
              (nextNode as HTMLElement).tagName === 'BR') {
            text += '\n'; // Only add one newline, the next <br> will add another
          } else {
            text += '\n';
          }
        } else {
          // For other elements (like DIV, P), recurse into their content
          text += getTextWithLineBreaks(elementNode);
          
          // Add newline after block elements (DIV, P) but not if we're at the end
          if ((elementNode.tagName === 'DIV' || elementNode.tagName === 'P') && 
              i < childNodes.length - 1) {
            text += '\n';
          }
        }
      }
    }
    
    return text;
  };

  // Update editor content when content prop changes externally (e.g., from AI generation or context sync)
  useEffect(() => {
    console.log(`EditableContentEditor useEffect for ${partId}: content="${content}", lastContent="${lastContent}"`);
    if (content !== lastContent && editorRef.current) {
      console.log(`EditableContentEditor: Updating DOM content for ${partId}:`, JSON.stringify(content));
      
      // Get current cursor position before updating
      const selection = window.getSelection();
      let cursorPosition = 0;
      
      if (selection && selection.rangeCount > 0 && editorRef.current.contains(selection.anchorNode)) {
        const range = selection.getRangeAt(0);
        cursorPosition = range.startOffset;
        
        // Calculate position relative to text content
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(editorRef.current);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        cursorPosition = preCaretRange.toString().length;
      }
      
      // Set flag to prevent triggering change events during programmatic update
      setIsUpdatingProgrammatically(true);
      
      // Only update if the content is significantly different (not just from user typing)
      const currentText = getTextWithLineBreaks(editorRef.current);
      const shouldUpdate = Math.abs(content.length - currentText.length) > 5 || 
                          content.replace(/\s+/g, ' ') !== currentText.replace(/\s+/g, ' ');
      
      if (shouldUpdate) {
        // Clear existing content first
        editorRef.current.innerHTML = '';
        
        if (content.trim()) {
          // Set content and format with <br> tags for consistency
          editorRef.current.innerHTML = content.replace(/\n/g, '<br>');
        }
        
        // Restore cursor position
        setTimeout(() => {
          if (editorRef.current && cursorPosition > 0) {
            try {
              const textNodes: Text[] = [];
              const walker = document.createTreeWalker(
                editorRef.current,
                NodeFilter.SHOW_TEXT,
                null
              );
              
              let node;
              while (node = walker.nextNode()) {
                textNodes.push(node as Text);
              }
              
              let currentOffset = 0;
              for (const textNode of textNodes) {
                const textLength = textNode.textContent?.length || 0;
                if (currentOffset + textLength >= cursorPosition) {
                  const range = document.createRange();
                  const selection = window.getSelection();
                  const offset = Math.min(cursorPosition - currentOffset, textLength);
                  range.setStart(textNode, offset);
                  range.setEnd(textNode, offset);
                  selection?.removeAllRanges();
                  selection?.addRange(range);
                  break;
                }
                currentOffset += textLength;
              }
            } catch (error) {
              console.log('Could not restore cursor position:', error);
            }
          }
        }, 0);
      }
      
      setLastContent(content);
      
      // Clear the flag after a short delay
      setTimeout(() => {
        setIsUpdatingProgrammatically(false);
      }, 20);
    }
  }, [content, lastContent, partId]);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    if (isUpdatingProgrammatically) return;
    
    console.log(`EditableContentEditor: Input detected for ${partId}`);
    if (editorRef.current) {
      const newContent = getTextWithLineBreaks(editorRef.current);
      console.log(`EditableContentEditor: Content extracted from DOM for ${partId}:`, JSON.stringify(newContent));
      onChange(newContent);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (isUpdatingProgrammatically) return;
    
    console.log(`EditableContentEditor: Blur detected for ${partId}`);
    if (editorRef.current) {
      const newContent = getTextWithLineBreaks(editorRef.current);
      console.log(`EditableContentEditor: Final content on blur for ${partId}:`, JSON.stringify(newContent));
      onChange(newContent);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      e.preventDefault();
      onEditorClick?.(partId);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    // Record the paste time
    const now = Date.now();
    setLastPasteTime(now);
    
    const pastedText = e.clipboardData.getData('text/plain');
    const target = e.target as HTMLElement;
    
    // Convert newlines to <br> tags for consistency
    const formattedText = pastedText.replace(/\n/g, '<br><br>');
    
    // Get the current selection/cursor position
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      // Delete any selected content first
      range.deleteContents();
      
      // Create a temporary div to hold the formatted HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = formattedText;
      
      // Insert each node from the temp div
      const fragment = document.createDocumentFragment();
      while (tempDiv.firstChild) {
        fragment.appendChild(tempDiv.firstChild);
      }
      
      // Insert the fragment at the cursor position
      range.insertNode(fragment);
      
      // Move cursor to the end of the inserted content
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // Fallback: if no selection, append to the end
      const currentHTML = target.innerHTML;
      target.innerHTML = currentHTML + formattedText;
    }
    
    // Get the complete text after paste
    const finalText = getTextWithLineBreaks(target);
    
    // Update the content immediately
    setLastContent(finalText);
    onChange(finalText);
    
    // Process content after a short delay to ensure DOM is updated
    setTimeout(() => {
      if (editorRef.current) {
        const updatedText = getTextWithLineBreaks(editorRef.current);
        console.log('Processing content after paste:', JSON.stringify(updatedText));
        setLastContent(updatedText);
        onChange(updatedText);
      }
    }, 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle Enter key to insert <br><br> instead of creating divs
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Create two <br> elements for line break
        const br1 = document.createElement('br');
        const br2 = document.createElement('br');
        
        // Insert the line breaks
        range.deleteContents();
        range.insertNode(br2);
        range.insertNode(br1);
        
        // Move cursor after the line breaks
        range.setStartAfter(br2);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Update content
        if (editorRef.current) {
          const text = getTextWithLineBreaks(editorRef.current);
          setLastContent(text);
          onChange(text);
        }
      }
      return;
    }
    
    // Don't process if it's just navigation or modifier keys
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
        e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Meta') {
      return;
    }
  };

  return (
    <ContentEditor
      ref={editorRef}
      contentEditable={!disabled}
      suppressContentEditableWarning={true}
      data-part-id={partId}
      onInput={handleInput}
      onBlur={handleBlur}
      onPaste={handlePaste}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      style={{
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'text'
      }}
    />
  );
};

const Write: React.FC = () => {
  const navigate = useNavigate();
  const { refreshCoins } = useCoins();
  const {
    selectedParts,
    customPartNames,
    collapsedParts,
    editingContent,
    setEditingContent,
    contentData,
    setContentData,
    togglePartCollapse,
    handlePartNameChange,
    getPartDisplayName,
    movePartUp,
    movePartDown,
    hasAccessedSpeech,
    setHasAccessedSpeech,
    isWriteReadOnly,
    setIsWriteReadOnly,
    segmentationMode,
    setSegmentationMode,
    saveScript,
    scriptDbId,
    partDbIds
  } = useScriptCreation();
  
  // State for AI generation modal
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiModalPartId, setAiModalPartId] = useState('');
  const [aiWordCount, setAiWordCount] = useState('300');
  const [aiUserPrompt, setAiUserPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGeneratedContent, setAiGeneratedContent] = useState('');
  const [aiPreviewMode, setAiPreviewMode] = useState(false);
  
  // State for save operation
  const [isSaving, setIsSaving] = useState(false);
  
  // State for improvement modal
  const [showImproveModal, setShowImproveModal] = useState(false);
  const [improveModalPartId, setImproveModalPartId] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [improvedContent, setImprovedContent] = useState('');
  const [isImproving, setIsImproving] = useState(false);
  
  // State for edit confirmation dialog
  const [showEditConfirmation, setShowEditConfirmation] = useState(false);
  const [confirmationPartId, setConfirmationPartId] = useState('');
  
  // State for Use Existing modal
  const [showUseExistingModal, setShowUseExistingModal] = useState(false);
  const [useExistingPartId, setUseExistingPartId] = useState('');

  const handleContentChange = (partId: string, content: string) => {
    console.log(`Write page: Content change for ${partId}:`, JSON.stringify(content));
    
    // Update editing content
    setEditingContent({
      ...editingContent,
      [partId]: content
    });
    
    // Clear any existing segments for this part since text has changed
    if (contentData[partId] && contentData[partId].length > 0) {
      console.log(`Write page: Clearing old segments after manual edit for ${partId}`);
      setContentData({
        ...contentData,
        [partId]: []
      });
    }
  };

  const handleEditorClick = (partId: string) => {
    // If Speech has been accessed and we're in read-only mode, show confirmation
    if (hasAccessedSpeech && isWriteReadOnly) {
      setConfirmationPartId(partId);
      setShowEditConfirmation(true);
    }
  };

  // AI Modal handlers
  const openAIModal = (partId: string) => {
    const part = meditationParts.find(p => p.id === partId);
    if (part) {
      setAiModalPartId(partId);
      setAiWordCount(part.defaultWords);
      setAiUserPrompt('');
      setShowAIModal(true);
    }
  };

  const closeAIModal = () => {
    setShowAIModal(false);
    setAiModalPartId('');
    setAiWordCount('');
    setAiUserPrompt('');
    setAiPreviewMode(false);
    setAiGeneratedContent('');
  };

  const handleGenerateAI = async () => {
    try {
      setAiGenerating(true);
      
      const part = meditationParts.find(p => p.id === aiModalPartId);
      if (!part) {
        console.error('Part not found');
        return;
      }

      // Get previous part content for context
      const currentIndex = selectedParts.indexOf(aiModalPartId);
      let previousPart = '';
      if (currentIndex > 0) {
        const previousPartId = selectedParts[currentIndex - 1];
        previousPart = editingContent[previousPartId] || '';
      }

      // Get auth token
      const token = authService.getToken();
      if (!token) {
        console.error('No auth token found');
        return;
      }

      // Make API call
      const response = await fetch(`${config.apiUrl}/generate-meditation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          partType: part.title,
          words: aiWordCount,
          userPrompt: aiUserPrompt,
          previousPart,
          systemPrompt: part.prompt
        })
      });

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 402) {
          const errorData = await response.json();
          alert(`Insufficient coins! ${errorData.error}\n\nPlease purchase more coins to continue using AI features.`);
        } else {
          throw new Error(`Failed to generate content (${response.status})`);
        }
        return;
      }

      const data = await response.json();
      
      // Enter preview mode with the generated content
      setAiGeneratedContent(data.content);
      setAiPreviewMode(true);
      
      // Refresh coins in header after successful generation
      await refreshCoins();
    } catch (error) {
      console.error('Error generating AI content:', error);
      alert('Failed to generate content. Please try again.');
      
      // Refresh coins on failure in case they were spent elsewhere
      await refreshCoins();
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAcceptGenerated = () => {
    console.log(`Write page: AI generated content accepted for ${aiModalPartId}`);
    
    // Update editing content with the generated content
    setEditingContent({
      ...editingContent,
      [aiModalPartId]: aiGeneratedContent
    });
    
    // Clear any existing segments for this part
    if (contentData[aiModalPartId] && contentData[aiModalPartId].length > 0) {
      console.log(`Write page: Clearing old segments after AI generation for ${aiModalPartId}`);
      setContentData({
        ...contentData,
        [aiModalPartId]: []
      });
    }

    closeAIModal();
  };

  const handleRegenerateAI = () => {
    setAiPreviewMode(false);
    setAiGeneratedContent('');
    // This will show the input form again
  };

  // Improvement Modal handlers
  const openImproveModal = (partId: string) => {
    const currentContent = editingContent[partId] || '';
    if (!currentContent.trim()) {
      // If no content, don't open the modal
      return;
    }
    
    setImproveModalPartId(partId);
    setOriginalContent(currentContent);
    setImprovedContent('');
    setShowImproveModal(true);
  };

  const closeImproveModal = () => {
    setShowImproveModal(false);
    setImproveModalPartId('');
    setOriginalContent('');
    setImprovedContent('');
    setIsImproving(false);
  };

  const handleImproveScript = async () => {
    try {
      setIsImproving(true);
      
      // Get auth token
      const token = authService.getToken();
      if (!token) {
        console.error('No auth token found');
        return;
      }

      // Make API call to improve the content
      const response = await fetch(`${config.apiUrl}/improve-meditation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: originalContent
        })
      });

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 402) {
          const errorData = await response.json();
          alert(`Insufficient coins! ${errorData.error}\n\nPlease purchase more coins to continue using AI features.`);
        } else {
          throw new Error(`Failed to improve content (${response.status})`);
        }
        return;
      }

      const data = await response.json();
      setImprovedContent(data.content);
      
      // Refresh coins in header after successful improvement
      await refreshCoins();
    } catch (error) {
      console.error('Error improving content:', error);
      alert('Failed to improve content. Please try again.');
      
      // Refresh coins on failure in case they were spent elsewhere
      await refreshCoins();
    } finally {
      setIsImproving(false);
    }
  };

  const handleAcceptImproved = () => {
    console.log(`Write page: Improved content accepted for ${improveModalPartId}`);
    
    // Update editing content with the improved content
    setEditingContent({
      ...editingContent,
      [improveModalPartId]: improvedContent
    });
    
    // Clear any existing segments for this part
    if (contentData[improveModalPartId] && contentData[improveModalPartId].length > 0) {
      console.log(`Write page: Clearing old segments after improvement for ${improveModalPartId}`);
      setContentData({
        ...contentData,
        [improveModalPartId]: []
      });
    }

    closeImproveModal();
  };

  const handleRevertToOriginal = () => {
    closeImproveModal();
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);
      
      // Save the script first
      await saveScript();
      
      // Enable Speech access and make Write read-only
      setHasAccessedSpeech(true);
      setIsWriteReadOnly(true);
      
      // Scroll to top of the page
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      navigate('../speech');
    } catch (error) {
      console.error('Failed to save script:', error);
      alert('Failed to save script. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmEdit = () => {
    // Reset Speech access and allow editing
    setHasAccessedSpeech(false);
    setIsWriteReadOnly(false);
    
    // Clear all speech generation data since user wants to edit
    setContentData({});
    
    setShowEditConfirmation(false);
    setConfirmationPartId('');
  };

  const handleCancelEdit = () => {
    setShowEditConfirmation(false);
    setConfirmationPartId('');
  };

  const handleSegmentationModeChange = (mode: 'punctuation' | 'sentence' | 'paragraph') => {
    // Only clear segments if the mode is actually changing
    if (mode !== segmentationMode) {
      console.log(`Segmentation mode changed from ${segmentationMode} to ${mode}, clearing segments to force recreation`);
      
      // Clear all existing segments to force recreation with new mode
      setContentData({});
      
      // Update the segmentation mode
      setSegmentationMode(mode);
    }
  };

  // Use Existing Modal handlers
  const openUseExistingModal = (partId: string) => {
    setUseExistingPartId(partId);
    setShowUseExistingModal(true);
  };

  const closeUseExistingModal = () => {
    setShowUseExistingModal(false);
    setUseExistingPartId('');
  };

  const handleSelectExistingPart = async (selectedPart: any) => {
    try {
      // Auto-save script if needed
      await saveScript();
      
      // Update the part content with the selected text
      console.log(`Write page: Using existing content for ${useExistingPartId}`);
      
      // Update editing content with the selected content
      setEditingContent({
        ...editingContent,
        [useExistingPartId]: selectedPart.script_part_text
      });
      
      // Clear any existing segments for this part since text has changed
      if (contentData[useExistingPartId] && contentData[useExistingPartId].length > 0) {
        console.log(`Write page: Clearing old segments after using existing part for ${useExistingPartId}`);
        setContentData({
          ...contentData,
          [useExistingPartId]: []
        });
      }
      
      // Update the part in the database with source tracking
      if (scriptDbId && partDbIds[useExistingPartId]) {
        await scriptService.updateScriptPartWithSource(scriptDbId, partDbIds[useExistingPartId], {
          script_part_text: selectedPart.script_part_text,
          source_part_id: selectedPart.id,
          source_script_id: selectedPart.script_id
        });
      }
      
      closeUseExistingModal();
    } catch (error) {
      console.error('Error using existing part:', error);
      alert('Failed to use existing part. Please try again.');
    }
  };

  if (selectedParts.length === 0) {
    return (
      <Container>
        <EmptyState>
          No parts selected. Please go back to the Plan page to select meditation parts.
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <ContentSection>
        {selectedParts.map((partId, index) => {
          const part = meditationParts.find(p => p.id === partId);
          if (!part) return null;
          
          const canMoveUp = index > 0;
          const canMoveDown = index < selectedParts.length - 1;
          const isCollapsed = collapsedParts.has(partId);
          
          return (
            <ContentBox key={partId}>
              <ContentHeader>
                <ContentTitleWithControls>
                  <ReorderControls>
                    <ReorderButton
                      onClick={() => movePartUp(partId)}
                      disabled={!canMoveUp}
                      title="Move up"
                    >
                      <Icon icon={FaChevronUp} />
                    </ReorderButton>
                    <ReorderButton
                      onClick={() => movePartDown(partId)}
                      disabled={!canMoveDown}
                      title="Move down"
                    >
                      <Icon icon={FaChevronDown} />
                    </ReorderButton>
                  </ReorderControls>
                  <ContentTitleInput
                    value={getPartDisplayName(partId, part.title)}
                    onChange={(e) => handlePartNameChange(partId, e.target.value)}
                    placeholder={part.title}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                  />
                </ContentTitleWithControls>
                <ContentButtons>
                  <ActionButton onClick={() => openAIModal(partId)}>
                    <Icon icon={FaMagic} />
                    Help me write it
                  </ActionButton>
                  <ActionButton onClick={() => openImproveModal(partId)}>
                    <Icon icon={FaWandMagicSparkles} />
                    Improve my script
                  </ActionButton>
                  <ActionButton onClick={() => openUseExistingModal(partId)}>
                    <Icon icon={FaFolderOpen} />
                    Use existing
                  </ActionButton>
                  <CollapseButton
                    onClick={() => togglePartCollapse(partId)}
                    title={isCollapsed ? 'Expand' : 'Collapse'}
                  >
                    {isCollapsed ? '+' : '−'}
                  </CollapseButton>
                </ContentButtons>
              </ContentHeader>
              
              {!isCollapsed && (
                <EditableContentEditor
                  partId={partId}
                  content={editingContent[partId] || ''}
                  onChange={(content) => handleContentChange(partId, content)}
                  disabled={isWriteReadOnly}
                  onEditorClick={handleEditorClick}
                />
              )}
            </ContentBox>
          );
        })}
      </ContentSection>

      <SegmentationModeContainer>
        <SegmentationModeHeader>
          <SegmentationModeTitle>Segmentation Mode</SegmentationModeTitle>
          <HelpButton 
            title="Segmentation Mode Help" 
            content={`
              <h2>Segmentation Mode</h2>
              <p>Choose how your script text will be broken down into segments for speech generation:</p>
              
              <h3>Sentence Mode</h3>
              <p><strong>Best for:</strong> Flowing, natural speech</p>
              <ul>
                <li>Speech generated for each complete sentence</li>
                <li>More fluid and consistent delivery</li>
                <li>Less control over pause timing within sentences</li>
              </ul>
              
              <h3>Punctuation Mode</h3>
              <p><strong>Best for:</strong> Fine-tuned pause control</p>
              <ul>
                <li>Speech generated by segments based on all punctuation</li>
                <li>Maximum control over pause timing within and between sentences</li>
                <li>Separate pauses for commas, periods, colons, etc.</li>
              </ul>
              
              <div class="alert info">
                <strong>Tip:</strong> You can change this setting anytime, but regenerating speech will be required if you switch modes.
              </div>
            `}
            size="medium"
          />
        </SegmentationModeHeader>
        
        <SegmentationModeOptions>
          <SegmentationModeOption 
            $selected={segmentationMode === 'sentence'}
            onClick={() => handleSegmentationModeChange('sentence')}
          >
            Sentence
          </SegmentationModeOption>
          <SegmentationModeOption 
            $selected={segmentationMode === 'punctuation'}
            onClick={() => handleSegmentationModeChange('punctuation')}
          >
            Punctuation
          </SegmentationModeOption>
        </SegmentationModeOptions>
        
        <SegmentationModeDescription>
          {segmentationMode === 'sentence' 
            ? 'Speech will be generated for each sentence. This gives you less versatility in pause timing within sentences, but speech is more fluid and consistent.'
            : 'Speech will be generated by segment based on all punctuation. This gives you the most versatility in pause timing within and between sentences.'
          }
        </SegmentationModeDescription>
      </SegmentationModeContainer>

      <WarningMessage>
        Please make sure script is finished before continuing to speech generation. Speech generation progress will be lost if you make major edits to the script.
      </WarningMessage>

      <ButtonContainer>
        <NextButton onClick={handleNext} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save and Continue'}
        </NextButton>
      </ButtonContainer>

      <HelpButton
        title="Create Your Meditation - Write"
        content={helpContent}
        position="fixed-bottom-right"
        />

      {/* AI Modal */}
      <Modal isOpen={showAIModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Help Me Write It</ModalTitle>
            <CloseButton onClick={closeAIModal}>×</CloseButton>
          </ModalHeader>
          
          {!aiPreviewMode ? (
            <>
              <SettingGroup>
                <SettingLabel>Approx. Word Count</SettingLabel>
                <SettingInput 
                  type="number" 
                  min="50" 
                  max="1000" 
                  value={aiWordCount}
                  onChange={(e) => setAiWordCount(e.target.value)}
                  placeholder="Enter word count"
                />
              </SettingGroup>
              
              <SettingGroup>
                <SettingLabel>Your Instructions </SettingLabel>
                <SettingTextArea
                  value={aiUserPrompt}
                  onChange={(e) => setAiUserPrompt(e.target.value)}
                  placeholder=""
                />
              </SettingGroup>

              <SettingGroup>
                <SettingLabel>Example</SettingLabel>
                <div style={{
                  padding: '8px',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  backgroundColor: '#333',
                  color: '#cccccc',
                  fontSize: '12px',
                  fontStyle: 'italic',
                  lineHeight: '1.4'
                }}>
                  {aiModalPartId && meditationParts.find(p => p.id === aiModalPartId)?.exampleUserPrompt}
                </div>
              </SettingGroup>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  onClick={closeAIModal}
                  style={{
                    background: 'none',
                    border: '1px solid #444',
                    color: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateAI}
                  disabled={aiGenerating}
                  style={{
                    backgroundColor: aiGenerating ? '#666' : '#007bff',
                    border: 'none',
                    color: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: aiGenerating ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {aiGenerating ? 'Generating...' : (<>Write It 30<img src="/images/YMcoin.png" alt="coin" style={{width: '16px', height: '16px', marginLeft: '2px', verticalAlign: 'middle'}} /></>)}
                </button>
              </div>
            </>
          ) : (
            <>
              <SettingGroup>
                <SettingLabel>Here's your script!</SettingLabel>
                <SettingTextArea
                  value={aiGeneratedContent}
                  readOnly
                  style={{ minHeight: '200px' }}
                />
              </SettingGroup>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  onClick={handleRegenerateAI}
                  style={{
                    backgroundColor: '#666',
                    border: 'none',
                    color: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Discard / Re-write it
                </button>
                <button
                  onClick={handleAcceptGenerated}
                  style={{
                    backgroundColor: '#28a745',
                    border: 'none',
                    color: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Continue
                </button>
              </div>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Improvement Modal */}
      <Modal isOpen={showImproveModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Improve My Script</ModalTitle>
            <CloseButton onClick={closeImproveModal}>×</CloseButton>
          </ModalHeader>
          
          {!improvedContent ? (
            <>
              <SettingGroup>
                <SettingLabel>Current Script</SettingLabel>
                <SettingTextArea
                  value={originalContent}
                  readOnly
                  style={{ minHeight: '200px' }}
                />
              </SettingGroup>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  onClick={closeImproveModal}
                  style={{
                    background: 'none',
                    border: '1px solid #444',
                    color: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleImproveScript}
                  disabled={isImproving}
                  style={{
                    backgroundColor: isImproving ? '#666' : '#007bff',
                    border: 'none',
                    color: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: isImproving ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {isImproving ? 'Improving...' : (<>Improve It 30<img src="/images/YMcoin.png" alt="coin" style={{width: '16px', height: '16px', marginLeft: '2px', verticalAlign: 'middle'}} /></>)}
                </button>
              </div>
            </>
          ) : (
            <>
              <SettingGroup>
                <SettingLabel>Original Script</SettingLabel>
                <SettingTextArea
                  value={originalContent}
                  readOnly
                  style={{ minHeight: '150px' }}
                />
              </SettingGroup>
              
              <SettingGroup>
                <SettingLabel>Improved Script</SettingLabel>
                <SettingTextArea
                  value={improvedContent}
                  readOnly
                  style={{ minHeight: '150px' }}
                />
              </SettingGroup>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  onClick={handleRevertToOriginal}
                  style={{
                    backgroundColor: '#666',
                    border: 'none',
                    color: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Revert to Original
                </button>
                <button
                  onClick={handleImproveScript}
                  disabled={isImproving}
                  style={{
                    backgroundColor: isImproving ? '#666' : '#666',
                    border: 'none',
                    color: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: isImproving ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {isImproving ? 'Re-writing...' : (<>Re-write it 30<img src="/images/YMcoin.png" alt="coin" style={{width: '16px', height: '16px', marginLeft: '2px', verticalAlign: 'middle'}} /></>)}
                </button>
                <button
                  onClick={handleAcceptImproved}
                  style={{
                    backgroundColor: '#28a745',
                    border: 'none',
                    color: '#ffffff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Continue
                </button>
              </div>
            </>
          )}
        </ModalContent>
      </Modal>
      
      {/* Edit Confirmation Dialog */}
      <ConfirmationModal isOpen={showEditConfirmation}>
        <ConfirmationContent>
          <ConfirmationTitle>Edit Script?</ConfirmationTitle>
          <ConfirmationText>
            Minor script edits should be performed on the Speech page. If you edit the script here, progress on the Speech generation page will be lost. Are you sure you want to continue?
          </ConfirmationText>
          <ConfirmationButtons>
            <ConfirmationButton onClick={handleCancelEdit}>
              No
            </ConfirmationButton>
            <ConfirmationButton variant="primary" onClick={handleConfirmEdit}>
              Yes
            </ConfirmationButton>
          </ConfirmationButtons>
        </ConfirmationContent>
      </ConfirmationModal>
      
      {/* Use Existing Modal */}
      <UseExistingModal
        isOpen={showUseExistingModal}
        onClose={closeUseExistingModal}
        onSelectPart={handleSelectExistingPart}
        partId={useExistingPartId}
      />
    </Container>
  );
};

export default Write; 